import { useCallback, useEffect, useState } from "react";
import axios, { API } from "@/lib/api";
import { Loader2, X } from "lucide-react";

const BACKEND = (typeof process !== "undefined" && process.env?.REACT_APP_BACKEND_URL) || "";

function FilterChip({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`text-[11px] uppercase tracking-[0.16em] px-3 py-1.5 rounded-full transition ${
        active
          ? "bg-[#D4AF37] text-black"
          : "border border-white/15 text-white/60 hover:text-white hover:border-white/30"
      }`}
    >
      {children}
    </button>
  );
}

function ViewerOverlay({ item, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-md flex items-center justify-center p-6 animate-in fade-in duration-200"
      onClick={onClose}
      data-testid="team-gallery-viewer"
    >
      <div onClick={(e) => e.stopPropagation()} className="relative max-w-4xl max-h-[88vh]">
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 w-8 h-8 rounded-full border border-white/15 text-white/75 hover:text-white flex items-center justify-center"
        >
          <X className="w-3.5 h-3.5" />
        </button>
        <img
          src={`${BACKEND}${item.image_url}${item.refined_at ? `?v=${encodeURIComponent(item.refined_at)}` : ""}`}
          alt={item.prompt}
          className="max-w-full max-h-[80vh] rounded-xl border border-white/10 object-contain"
        />
        <div className="mt-3 text-center">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">{item.owner_name}</div>
          <div className="text-xs text-white/65 mt-1 max-w-2xl mx-auto leading-relaxed">{item.prompt}</div>
        </div>
      </div>
    </div>
  );
}

/**
 * Team gallery tab — lead's read-only oversight view of every member's
 * recent renders. Filter by member via the chips strip; click any tile
 * to open it large.
 */
export default function TeamGalleryTab({ workspaceId }) {
  const [items, setItems] = useState([]);
  const [members, setMembers] = useState([]);
  const [memberId, setMemberId] = useState("");
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [active, setActive] = useState(null);
  const PAGE = 40;

  const load = useCallback(async (mid, off) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(PAGE), offset: String(off) });
      if (mid) params.set("member_id", mid);
      const r = await axios.get(
        `${API}/workspaces/${workspaceId}/gallery?${params.toString()}`,
      );
      const data = r.data || {};
      setMembers(data.members || []);
      setHasMore(!!data.has_more);
      setItems((prev) => (off === 0 ? data.items : [...prev, ...(data.items || [])]));
    } catch (err) {
      console.warn("gallery load failed", err);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { setOffset(0); load(memberId, 0); }, [memberId, load]);

  return (
    <div className="max-w-5xl mt-6">
      <div className="flex flex-wrap items-center gap-1.5 mb-4" data-testid="team-gallery-filters">
        <FilterChip active={!memberId} onClick={() => setMemberId("")}>
          All designers
        </FilterChip>
        {members.map((m) => (
          <FilterChip
            key={m.id}
            active={memberId === m.id}
            onClick={() => setMemberId(m.id)}
            testId={`team-gallery-chip-${m.id}`}
          >
            {m.name}
          </FilterChip>
        ))}
      </div>
      {loading && items.length === 0 ? (
        <div className="py-12 text-center text-sm text-white/45 inline-flex items-center justify-center gap-2 w-full">
          <Loader2 className="w-4 h-4 animate-spin text-[#D4AF37]" /> Loading team gallery…
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-white/10 bg-[#0E0E0E] py-14 text-center text-sm text-white/55" data-testid="team-gallery-empty">
          Nothing here yet. Once your designers create renders, they&apos;ll
          show up in this oversight view.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="team-gallery-grid">
            {items.map((g) => (
              <button
                key={g.id}
                type="button"
                onClick={() => setActive(g)}
                data-testid={`team-gallery-tile-${g.id}`}
                style={{ aspectRatio: "1 / 1" }}
                className="relative block w-full rounded-lg overflow-hidden border border-white/10 hover:border-[#D4AF37]/45 transition group bg-[#0A0A0A]"
                title={`${g.owner_name} · ${g.prompt}`}
              >
                <img
                  src={`${BACKEND}${g.image_url}${g.refined_at ? `?v=${encodeURIComponent(g.refined_at)}` : ""}`}
                  alt={g.prompt}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-black/90 to-transparent pointer-events-none" />
                <span className="absolute bottom-1.5 left-2 text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] font-medium leading-none">
                  {g.owner_name}
                </span>
                {g.render_kind === "cad_ready" && (
                  <span className="absolute top-1.5 right-1.5 text-[8.5px] uppercase tracking-[0.18em] text-emerald-300 bg-black/70 px-1.5 py-0.5 rounded-full border border-emerald-400/30">
                    CAD
                  </span>
                )}
              </button>
            ))}
          </div>
          {hasMore && (
            <div className="mt-5 text-center">
              <button
                type="button"
                disabled={loading}
                onClick={() => {
                  const next = offset + PAGE;
                  setOffset(next);
                  load(memberId, next);
                }}
                data-testid="team-gallery-load-more"
                className="text-xs px-4 py-2 rounded-full border border-white/15 text-white/75 hover:text-[#D4AF37] hover:border-[#D4AF37]/40 transition disabled:opacity-50"
              >
                {loading ? "Loading…" : "Load more"}
              </button>
            </div>
          )}
        </>
      )}
      {active && (
        <ViewerOverlay item={active} onClose={() => setActive(null)} />
      )}
    </div>
  );
}
